import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Form,
  ListGroup,
  Spinner,
  Image,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { useHttpClient } from "../../hooks/http-hook";
import { useAuth } from "../../Context/AuthContext";
import { useTranslation } from "react-i18next"; // Importer useTranslation
import logo from "../../assets/python.png";

export default function SearchModal({ show, handleClose }) {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const { sendRequest, isLoading } = useHttpClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredResults, setFilteredResults] = useState();
  const [results, setResults] = useState([]);
  const navigate = useNavigate();
  const { authData } = useAuth();

  useEffect(() => {
    if (searchQuery.length >= 1) {
      const fetchResults = async () => {
        try {
          const response = await sendRequest(
            `${
              process.env.REACT_APP_BACKEND_URL
            }/search?query=${encodeURIComponent(searchQuery)}`,
            "GET",
            null,
            {
              "Content-Type": "application/json",
            },
            true
          );

          let searchResults = [];
          if (response) {
            if (Array.isArray(response)) {
              searchResults = response;
            } else if (typeof response === "object") {
              if (
                response.ok &&
                Object.keys(response).some((key) => !isNaN(parseInt(key)))
              ) {
                searchResults = Object.keys(response)
                  .filter((key) => !isNaN(parseInt(key)))
                  .map((key) => response[key]);
              } else {
                searchResults = response.results || response.data || response;
              }
            }
          }

          setResults(searchResults);
        } catch (error) {
          console.error(t("search_error"), error);
        }
      };
      fetchResults();
    } else {
      setResults([]);
    }
  }, [searchQuery, sendRequest, t]);

  useEffect(() => {
    if (results && results.length > 0) {
      const filtered = results.filter((item) => {
        if (item.is_public) {
          return true;
        }

        if (item.created_by && authData.userId === item.created_by._id) {
          return true;
        }

        return false;
      });

      setFilteredResults(filtered);
    } else {
      setFilteredResults([]);
    }
  }, [results, authData.userId]);

  const handleSelect = (id) => {
    setSearchQuery("");
    setResults([]);
    navigate(`/programs/${id}`);
    handleClose();
  };

  const handleModalClose = () => {
    setSearchQuery("");
    setResults([]);
    handleClose();
  };

  const defaultImage = logo;

  return (
    <Modal show={show} onHide={handleModalClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t("search_title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Control
          type="text"
          placeholder={t("search_placeholder")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
          className="mb-3"
        />
        {isLoading && (
          <div className="text-center my-3">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t("loading")}</span>
            </Spinner>
          </div>
        )}

        <ListGroup className="search-results">
          {results && results.length > 0
            ? results.map((item) => (
                <ListGroup.Item
                  key={item._id}
                  action
                  onClick={() => handleSelect(item._id)}
                  className="py-3"
                >
                  <Row className="align-items-center">
                    <Col xs={3} sm={2} className="text-center">
                      <Image
                        src={item.imageUrl || defaultImage}
                        alt={item.title}
                        width={80}
                        height={80}
                        className="rounded"
                        style={{ objectFit: "cover" }}
                        onError={(e) => {
                          e.target.src = defaultImage;
                        }}
                      />
                    </Col>
                    <Col>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-1">{item.title}</h5>
                        <Badge bg={item.is_public ? "success" : "secondary"}>
                          {item.is_public
                            ? t("public_badge")
                            : t("private_badge")}
                        </Badge>
                      </div>
                      <p className="mb-1 text-muted small">
                        {item.description
                          ?.replace(/<[^>]*>/g, "")
                          .substring(0, 100)}
                        ...
                      </p>
                      <div className="d-flex mt-1">
                        <Badge bg="primary" className="me-2">
                          {item.categorie}
                        </Badge>
                        <Badge bg="info">{item.level}</Badge>
                        {item.created_by && (
                          <span className="ms-auto small text-muted">
                            {t("created_by")}: {item.created_by.first_name}{" "}
                            {item.created_by.last_name}
                          </span>
                        )}
                      </div>
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))
            : searchQuery.length >= 1 &&
              !isLoading && (
                <ListGroup.Item className="text-center py-4">
                  {t("no_results_found")}
                </ListGroup.Item>
              )}
        </ListGroup>
      </Modal.Body>
    </Modal>
  );
}
